<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { McSwitch } from "@maersk-global/mds-components-core";
import { useFiltersStore } from "@/store";
import { FilterType, FilterTypeEnum, useApplyFilter, FilterDefinition } from "@/features/filters";
import { useI18n } from "vue-i18n";
import { isFilterDisabled } from "@/features/filters/utils/isFilterDisabled";

const props = defineProps<{
  filter: FilterDefinition;
  filterType: FilterType;
  zonePrefix?: string;
}>();

const {
  key: filterKey,
  label: filterLabel = "",
  sectionLabel,
  groupId,
  groupLabel,
  disableOnOtherFilters
} = props.filter;

const { t } = useI18n();

const zone = props.zonePrefix || "default";

const filtersStore = useFiltersStore();
const { filtersApplied, panelSelectedFilters, applyPanelSelectedFilters } = filtersStore;
const { applyFilter, removeFilter } = useApplyFilter(props.filterType, props.zonePrefix);

const switchValue = ref(false);
// Computed: selected filter from store
const selectedFilter = computed(() => {
  const source = props.filterType === FilterTypeEnum.PANEL_FILTERS ? panelSelectedFilters : filtersApplied;
  const storeValue = props.filter.notApplicable ? undefined : source?.[zone]?.[filterKey];
  return {
    ...(storeValue || { label: "", value: false }),
    filterType: props.filterType
  };
});

// Computed: UI switch checked value
const switchChecked = computed(() => {
  return switchValue.value;
});

// Sync switch value with store
watch(
  selectedFilter,
  (val) => {
    switchValue.value = val?.value || false;
  },
  { immediate: true }
);

// Build filter data
const buildFilterData = (checked: boolean) => {
  if (!checked) return null;

  return {
    filterKey,
    label: t(filterLabel),
    value: checked,
    filterType: props.filterType,
    ...(groupId ? { groupId } : {}),
    ...(groupLabel ? { groupLabel } : {}),
    ...(sectionLabel ? { sectionLabel } : {}),
    ...(filterLabel ? { filterLabel } : {})
  };
};

// Update panel filters
const updatePanelFilters = (filterData: any) => {
  if (!panelSelectedFilters[zone]) panelSelectedFilters[zone] = {};
  applyPanelSelectedFilters(zone, filterKey, filterData);
};

// Update applied filters
const updateAppliedFilters = (filterData: any) => {
  if (filterData) {
    applyFilter(filterKey, filterData);
  } else {
    removeFilter(filterKey);
  }
};
/**
 * Clear dependent filters when the current filter is changed/updated
 */
const clearDependencies = () => {
  if (props.filter.clearDependencies) {
    props.filter.clearDependencies?.forEach((dependentFilterKey) => {
      applyPanelSelectedFilters(zone, dependentFilterKey, null);
    });
  }
};

// Handle switch change
const onSwitchChange = (e: Event) => {
  const checked = (e.target as HTMLInputElement).checked;
  switchValue.value = checked;

  const filterData = buildFilterData(checked);
  if (checked) {
    clearDependencies();
  }

  if (props.filterType === FilterTypeEnum.PANEL_FILTERS) {
    updatePanelFilters(filterData);
  } else {
    updateAppliedFilters(filterData);
  }
};

const isDisabled = computed(() => {
  const source = props.filterType === FilterTypeEnum.PANEL_FILTERS ? panelSelectedFilters : filtersApplied;
  return isFilterDisabled(props.filter.disabled, disableOnOtherFilters, source?.[zone]);
});
</script>

<template>
  <mc-switch
    slot="trigger"
    :name="filterKey"
    :label="t(filterLabel)"
    :checked="switchChecked"
    data-testid="switch-filter"
    :disabled="isDisabled"
    fit="small"
    @change="onSwitchChange"
  />
</template>
